import { Component, OnInit, OnDestroy, OnChanges, SimpleChanges, Input, Output, EventEmitter } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { FilterConfig, FilterType, FilterData } from '../../models/filter-config.interface';

@Component({
    selector: 'pv-dynamic-filters',
    templateUrl: './dynamic-filters.component.html',
    styleUrls: ['./dynamic-filters.component.scss'],
    standalone: false
})
export class DynamicFiltersComponent implements OnInit, OnDestroy, OnChanges {

  @Input() filtersConfig: FilterConfig[] = [];
  @Input() initialValues: FilterData = {};
  @Input() showClearButton: boolean = true;
  @Output() filtersChange = new EventEmitter<FilterData>();
  @Output() clearFilters = new EventEmitter<void>();

  public form: UntypedFormGroup;
  public FilterType = FilterType;
  private destroy$ = new Subject<void>();
  private isClearingInternally = false;
  private isSubscriptionSetup = false;
  private hasInitialValuesBeenSet = false;

  constructor(private fb: UntypedFormBuilder) {}

  ngOnInit(): void {
    if (this.filtersConfig && this.filtersConfig.length > 0) {
      this.buildForm();
      this.setupFormSubscription();
      this.isSubscriptionSetup = true;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.filtersConfig && changes.filtersConfig.currentValue) {
      this.buildForm();
      this.setupFormSubscription();
      this.isSubscriptionSetup = true;
    }
    
    if (changes.initialValues && changes.initialValues.currentValue && this.form && !this.hasInitialValuesBeenSet) {
      const newValues = changes.initialValues.currentValue;
      
      if (Object.keys(newValues).some(key => newValues[key])) {
        this.updateFormValues(newValues);
        this.hasInitialValuesBeenSet = true;
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private buildForm(): void {
    if (!this.filtersConfig || this.filtersConfig.length === 0) {
      return;
    }
    
    const formControls = {};
    
    this.filtersConfig.forEach(config => {
      const initialValue = this.initialValues[config.key] || config.defaultValue || '';
      formControls[config.key] = [{ value: initialValue, disabled: !this.isFieldEnabled(config) }, config.validators || []];
      
      if (config.type === FilterType.DATE_RANGE) {
        formControls[config.key + '_from'] = [this.initialValues[config.key + '_from'] || '', config.validators || []];
        formControls[config.key + '_to'] = [this.initialValues[config.key + '_to'] || '', config.validators || []];
      }
    });

    this.form = this.fb.group(formControls);
  }

  private updateFormValues(newValues: FilterData): void {
    if (!this.form) {
      return;
    }
    
    this.isClearingInternally = true;
    
    Object.keys(newValues).forEach(key => {
      const control = this.form.get(key);
      const filterConfig = this.filtersConfig.find(f => f.key === key);
      
      if (control && newValues[key] !== null && newValues[key] !== undefined) {
        const value = newValues[key];
        
        if (Array.isArray(value) && filterConfig?.type === FilterType.SELECT) {
          control.setValue(value.length > 0 ? value[0] : '');
        } else if (Array.isArray(value) && filterConfig?.type === FilterType.MULTI_SELECT) {
          control.setValue(value);
        } else if (!Array.isArray(value)) {
          control.setValue(value);
        } else {
          control.setValue(Array.isArray(value) ? (value.length > 0 ? value[0] : '') : value);
        }
      }
    });
    
    setTimeout(() => {
      this.isClearingInternally = false;
    }, 400);
  }

  private setupFormSubscription(): void {
    if (!this.form) {
      return;
    }
    this.destroy$.next();
    this.form.valueChanges
      .pipe(
        debounceTime(300),
        takeUntil(this.destroy$)
      )
      .subscribe(values => {
        if (!this.isClearingInternally) {
          this.filtersChange.emit(values);
        }
      });
  }

  public getSortedFilters(): FilterConfig[] {
    return this.filtersConfig.sort((a, b) => (a.order || 0) - (b.order || 0));
  }

  public onClearFilters(): void {
    this.resetForm();
    this.clearFilters.emit();
  }

  public resetForm(): void {    
    if (!this.form) {
      return;
    }
    
    this.isClearingInternally = true;
    
    const resetValues = {};
    this.filtersConfig.forEach(config => {
      const defaultValue = config.defaultValue || '';
      resetValues[config.key] = defaultValue;
      
      if (config.type === FilterType.DATE_RANGE) {
        resetValues[config.key + '_from'] = '';
        resetValues[config.key + '_to'] = '';
      }
    });
        
    this.form.reset(resetValues);
    
    Object.keys(this.form.controls).forEach(key => {
      const control = this.form.get(key);
      if (control) {
        control.markAsUntouched();
        control.markAsPristine();
        control.setValue(resetValues[key] || '');
      }
    });
    
    this.form.updateValueAndValidity();
    
    this.hasInitialValuesBeenSet = false;
    
    setTimeout(() => {
      this.isClearingInternally = false;
    }, 100);
    
    this.filtersChange.emit(resetValues);
  }

  public isFieldVisible(config: FilterConfig): boolean {
    if (!config.dependencies) return true;
    if (!this.form) return true;

    return config.dependencies.every(dep => {
      const dependentValue = this.form.get(dep.dependsOn)?.value;
      const conditionMet = dep.condition(dependentValue);
      return dep.action === 'show' ? conditionMet : !conditionMet;
    });
  }

  public isFieldEnabled(config: FilterConfig): boolean {
    if (!config.dependencies) return true;
    if (!this.form) return true;

    return config.dependencies.every(dep => {
      const dependentValue = this.form.get(dep.dependsOn)?.value;
      const conditionMet = dep.condition(dependentValue);
      return dep.action === 'enable' ? conditionMet : !conditionMet;
    });
  }
}
